import { Injectable } from '@angular/core';
import { IMessage } from '@stomp/stompjs';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged, filter } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { StompService } from 'src/app/core/services/stomp.service';
import { EnvService } from 'src/app/core/helpers/env.service';
import { ApiHelperService } from 'src/app/core/helpers/api-helper.service';
import { AppStateService } from 'src/app/core/services/app-state.service';
import type {
    HeaderNotificationItem,
    NotificationApiRecord,
    NotificationsPageResponse,
} from 'src/app/shared/models/notification.interface';

/** Server list pagination (Spring `Page`) + loading flag for the header dropdown. */
export interface NotificationListPaginationState {
    page: number;
    totalPages: number;
    size: number;
    last: boolean;
    loading: boolean;
}

/**
 * STOMP subscriptions + REST on {@link EnvService.WS_BASE_URL}.
 * Notify REST uses {@link ApiHelperService} with **`hostnameOption: 'WS_BASE_URL'`** (see {@link RestConnector.getHostname})
 * and **`Authorization`** (`jwt` from `localStorage`) on fetch/mark calls.
 */
@Injectable({
    providedIn: 'root',
})
export class NotificationWsService {
    /** Must match {@link RestConnector.getHostname} `case` value. */
    private static readonly NOTIFY_HOST_OPTION = 'WS_BASE_URL';

    /** Page size for `GET /api/notifications` (header dropdown + load more). */
    readonly pageSize = 10;

    readonly notifications$ = new BehaviorSubject<HeaderNotificationItem[]>([]);
    readonly unreadCount$ = new BehaviorSubject<number>(0);
    readonly listPagination$ = new BehaviorSubject<NotificationListPaginationState>({
        page: 0,
        totalPages: 0,
        size: 10,
        last: true,
        loading: false,
    });

    constructor(
        private stomp: StompService,
        private env: EnvService,
        private api: ApiHelperService,
        private appState: AppStateService,
    ) {
        this.stomp
            .watch(environment.STOMP_USER_QUEUE_NOTIFICATIONS)
            .subscribe((msg) => this.onNotificationMessage(msg));
        this.stomp
            .watch(environment.STOMP_USER_QUEUE_UNREAD_COUNT)
            .subscribe((msg) => this.onUnreadMessage(msg));

        this.stomp
            .getConnectionState()
            .pipe(distinctUntilChanged(), filter(Boolean))
            .subscribe(() => {
                this.fetchUnreadCount();
                this.fetchNotificationList();
            });

        this.appState.tokenSubject$.subscribe(() => {
            if (!this.hasToken()) {
                this.clearAll();
            }
        });
    }

    /**
     * Same idea as the HTML demo’s `doDisconnect()` — drops STOMP/SockJS only.
     * Logout/session is still handled app-wide (e.g. {@link AppComponent}).
     */
    disconnectStomp(): void {
        this.stomp.disconnect();
    }

    /** `GET /api/notifications/unread-count` */
    fetchUnreadCount(): void {
        if (!this.hasToken() || !this.hasNotifyHost()) {
            return;
        }
        this.api.getAll(environment.NOTIFICATION_GET_UNREAD_COUNT)
            .subscribe((body) => {
                if (body == null) {
                    return;
                }
                this.applyUnreadFromApi(body);
            });
    }

    /**
     * `GET /api/notifications?page&size`
     * @param mode `replace` — first page / refresh; `append` — load next page (deduped by `id`).
     */
    fetchNotificationList(
        page = 0,
        size = this.pageSize,
        mode: 'replace' | 'append' = 'replace',
    ): void {
        if (!this.hasToken() || !this.hasNotifyHost()) {
            return;
        }
        this.listPagination$.next({
            ...this.listPagination$.value,
            loading: true,
        });
        this.api.getAll(environment.NOTIFICATION_GET_ALL, { page, size })
            .subscribe({
                next: (raw) => {
                    if (raw == null) {
                        this.listPagination$.next({
                            ...this.listPagination$.value,
                            loading: false,
                        });
                        return;
                    }
                    const pageData = this.unwrapNotificationListPage(raw);
                    if (!pageData) {
                        this.listPagination$.next({
                            ...this.listPagination$.value,
                            loading: false,
                        });
                        return;
                    }

                    const rows = pageData.content?.map((row) => this.mapApiRowToHeader(row)) ?? [];

                    if (mode === 'append') {
                        const current = this.notifications$.value;
                        const seen = new Set(current.map((n) => n.id));
                        const merged = [...current];
                        for (const row of rows) {
                            if (!seen.has(row.id)) {
                                seen.add(row.id);
                                merged.push(row);
                            }
                        }
                        this.notifications$.next(merged);
                    } else {
                        this.notifications$.next(rows);
                    }

                    const last =
                        pageData?.last ??
                        (pageData?.totalPages != null && pageData.totalPages > 0
                            ? page >= pageData.totalPages - 1
                            : true);

                    this.listPagination$.next({
                        page: pageData?.number ?? page,
                        totalPages: pageData?.totalPages ?? 0,
                        size: pageData?.size ?? size,
                        last,
                        loading: false,
                    });
                },
                error: () => {
                    this.listPagination$.next({
                        ...this.listPagination$.value,
                        loading: false,
                    });
                },
            });
    }

    private onNotificationMessage(msg: IMessage): void {
        const item = this.parseNotification(msg.body);
        if (!item) {
            return;
        }
        const current = this.notifications$.value;
        const withoutDup = current.filter((n) => n.id !== item.id);
        this.notifications$.next([item, ...withoutDup].slice(0, 200));
    }

    private onUnreadMessage(msg: IMessage): void {
        const n = this.parseUnreadCount(msg.body);
        if (n != null && n >= 0) {
            this.unreadCount$.next(n);
        }
    }

    parseUnreadCount(body: string): number | null {
        const trimmed = body?.trim();
        if (!trimmed) {
            return null;
        }
        try {
            const o = JSON.parse(trimmed) as unknown;
            if (typeof o === 'number') {
                return o;
            }
            if (o && typeof o === 'object') {
                const rec = o as Record<string, unknown>;
                if (typeof rec['data'] === 'number') {
                    return rec['data'];
                }
                if (typeof rec['count'] === 'number') {
                    return rec['count'];
                }
                if (typeof rec['unreadCount'] === 'number') {
                    return rec['unreadCount'];
                }
            }
        } catch {
            const num = parseInt(trimmed, 10);
            if (!Number.isNaN(num)) {
                return num;
            }
        }
        return null;
    }

    parseNotification(body: string): HeaderNotificationItem | null {
        const trimmed = body?.trim();
        if (!trimmed) {
            return null;
        }
        try {
            const o = JSON.parse(trimmed) as NotificationApiRecord;
            const message = String(
                o.message ??
                    o.content ??
                    o.text ??
                    o.body ??
                    o.title ??
                    trimmed,
            );
            const created =
                o.createdTime ??
                o.createdAt ??
                o.timestamp ??
                o.created_at ??
                o.time;
            return {
                id: String(o.id ?? `n-${Date.now()}-${Math.random()}`),
                message,
                read: Boolean(o.isRead ?? o.read),
                createdAt: this.parseNotifyCreatedAt(created),
                avatarUrl: o.avatarUrl ?? o.avatar,
                title: o.title,
                link: o.actionUrl ?? o.link ?? o.url,
            };
        } catch {
            return {
                id: `n-${Date.now()}`,
                message: trimmed,
                read: false,
                createdAt: new Date(),
            };
        }
    }

    /** `PUT /api/notifications/read-all` then refresh list. */
    markAllRead(): void {
        if (!this.hasToken() || !this.hasNotifyHost()) {
            this.notifications$.next(
                this.notifications$.value.map((n) => ({ ...n, read: true })),
            );
            this.unreadCount$.next(0);
            return;
        }
        this.api.update(environment.NOTIFICATION_READ_ALL, {})
            .subscribe((res) => {
                if (res == null) {
                    return;
                }
                this.unreadCount$.next(0);
                this.notifications$.next(
                    this.notifications$.value.map((n) => ({ ...n, read: true })),
                );
                this.fetchNotificationList();
            });
    }

    clearAll(): void {
        this.notifications$.next([]);
        this.unreadCount$.next(0);
        this.listPagination$.next({
            page: 0,
            totalPages: 0,
            size: this.pageSize,
            last: true,
            loading: false,
        });
    }

    /** `PUT /api/notifications/:id/read` then reload list (demo behaviour). */
    markOneRead(id: string): void {
        if (!this.hasToken() || !this.hasNotifyHost()) {
            this.applyMarkOneReadLocal(id);
            return;
        }
        this.api.updateById(environment.NOTIFICATION_READ_ONE, encodeURIComponent(id), {})
            .subscribe((res) => {
                if (res == null) {
                    return;
                }
                this.fetchNotificationList();
                this.fetchUnreadCount();
            });
    }

    private applyMarkOneReadLocal(id: string): void {
        const list = this.notifications$.value.map((n) =>
            n.id === id ? { ...n, read: true } : n,
        );
        this.notifications$.next(list);
        this.unreadCount$.next(list.filter((x) => !x.read).length);
    }

    private hasNotifyHost(): boolean {
        return Boolean((this.env.WS_BASE_URL || '').trim());
    }

    private hasToken(): boolean {
        return Boolean(localStorage.getItem('token'));
    }

    private applyUnreadFromApi(body: unknown): void {
        if (typeof body === 'number' && !Number.isNaN(body)) {
            this.unreadCount$.next(body);
            return;
        }
        if (body && typeof body === 'object') {
            const rec = body as Record<string, unknown>;
            if (typeof rec['data'] === 'number' && !Number.isNaN(rec['data'])) {
                this.unreadCount$.next(rec['data']);
                return;
            }
            const c = rec['count'] ?? rec['unreadCount'];
            if (typeof c === 'number') {
                this.unreadCount$.next(c);
                return;
            }
        }
        this.unreadCount$.next(0);
    }

    /** Unwrap `data` when the gateway returns {@link NotificationListResponse}, else treat as a raw page. */
    private unwrapNotificationListPage(body: unknown): NotificationsPageResponse | null {
        if (!body || typeof body !== 'object') {
            return null;
        }
        const o = body as Record<string, unknown>;
        if (
            'data' in o &&
            o['data'] != null &&
            typeof o['data'] === 'object' &&
            'content' in (o['data'] as object)
        ) {
            return o['data'] as NotificationsPageResponse;
        }
        if ('content' in o) {
            return o as unknown as NotificationsPageResponse;
        }
        return null;
    }

    private mapApiRowToHeader(n: NotificationApiRecord): HeaderNotificationItem {
        const created =
            n.createdTime ?? n.createdAt ?? n.timestamp ?? n.created_at;
        return {
            id: String(n.id ?? ''),
            message: String(
                n.content ?? n.message ?? n.title ?? n.body ?? '',
            ),
            read: Boolean(n.isRead ?? n.read),
            createdAt: this.parseNotifyCreatedAt(created),
            avatarUrl: n.avatarUrl ?? n.avatar,
            title: n.title,
            link: n.actionUrl ?? n.link ?? n.url,
        };
    }

    /**
     * Notify APIs often send GMT+0 / UTC instants without a timezone suffix.
     * `Date` parses those as local wall time in browsers, which skews “time ago”.
     * If there is no explicit zone (`Z` or `±hh:mm`), treat the value as UTC.
     */
    private parseNotifyCreatedAt(
        created: string | number | undefined | null,
    ): Date {
        if (created == null || created === '') {
            return new Date();
        }
        if (typeof created === 'number') {
            return new Date(created);
        }
        const s = String(created).trim();
        if (!s) {
            return new Date();
        }
        if (/Z$/i.test(s) || /[+-]\d{2}:\d{2}$/.test(s)) {
            return new Date(s);
        }
        const normalized = s.includes('T') ? s : s.replace(/^(\d{4}-\d{2}-\d{2})\s+/, '$1T');
        if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(normalized)) {
            return new Date(`${normalized}Z`);
        }
        const d = new Date(s);
        return Number.isNaN(d.getTime()) ? new Date() : d;
    }
}
